import type { ComponentType } from 'react';
import { ChevronRight } from 'lucide-react';
import type { QueueItem } from '../stats';
import WidgetCard, { EmptyLine, displayEmployee } from './WidgetCard';

interface QueueCardProps {
  title: string;
  icon: ComponentType<{ className?: string }>;
  items: QueueItem[];
  loading?: boolean;
  emptyText: string;
  /** Label for the person column, e.g. "Acct Mgr" */
  assigneeLabel: string;
  /** Format the optional detail (e.g. a YYYY-MM month) */
  formatDetail?: (detail: string) => string;
  onOpenItem: (rowId: string) => void;
  onViewAll: () => void;
}

/**
 * A clickable list of every account that needs attention. The list fills
 * the card's height (set by the taller cards sharing its grid row) and
 * scrolls inside it: the scroll area is absolutely positioned so the list
 * itself never stretches the row, with a floor of about five rows when the
 * card stands alone.
 */
export default function QueueCard({
  title,
  icon,
  items,
  loading,
  emptyText,
  assigneeLabel,
  formatDetail,
  onOpenItem,
  onViewAll,
}: Readonly<QueueCardProps>) {
  return (
    <WidgetCard
      title={title}
      icon={icon}
      badge={loading ? undefined : items.length}
      loading={loading}
      action={
        items.length > 0 ? { label: 'View all', onClick: onViewAll } : undefined
      }
    >
      {items.length === 0 ? (
        <EmptyLine>{emptyText}</EmptyLine>
      ) : (
        <div className="relative min-h-72 flex-1">
          <ul className="absolute -inset-x-2 inset-y-0 divide-y divide-slate-100 overflow-y-auto overscroll-contain">
            {items.map(item => (
              <li key={item.rowId}>
                <button
                  type="button"
                  onClick={() => onOpenItem(item.rowId)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-modern-charcoal"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-900">
                      {item.name}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {assigneeLabel}: {displayEmployee(item.assignee)}
                      {item.detail &&
                        ` · ${formatDetail ? formatDetail(item.detail) : item.detail}`}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </WidgetCard>
  );
}
